import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#165581] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/login" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to eSay
          </Link>
          <h1 className="text-4xl font-bold">Privacy Policy</h1>
          <p className="text-white/80 mt-2">Last updated: April 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              Welcome to eSay ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and 
              safeguard your information when you use our email marketing platform at esay.co.uk (the "Service").
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy 
              policy, please do not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2.1 Personal Information You Provide</h3>
            <p className="text-slate-600 leading-relaxed">We collect information that you voluntarily provide when you:</p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Register for an account (name, email address, password)</li>
              <li>Connect your Gmail account for sending emails</li>
              <li>Create and manage email campaigns</li>
              <li>Upload or import contact lists</li>
              <li>Subscribe to our paid plans (payment information processed by Stripe)</li>
              <li>Contact us for support</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2.2 Information from Third-Party Services</h3>
            <p className="text-slate-600 leading-relaxed">When you connect third-party services, we may receive:</p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li><strong>Google/Gmail:</strong> Email address, profile information, and permission to send emails on your behalf</li>
              <li><strong>Business Search (Outscraper):</strong> Publicly available business information including names, addresses, phone numbers, and email addresses</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">2.3 Automatically Collected Information</h3>
            <p className="text-slate-600 leading-relaxed">We automatically collect certain information when you use the Service:</p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Email campaign analytics (open rates, click rates, bounce rates)</li>
              <li>Device information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 leading-relaxed">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Provide, operate, and maintain the Service</li>
              <li>Send emails on your behalf through your connected Gmail account</li>
              <li>Process your transactions and manage your subscriptions</li>
              <li>Provide analytics on your email campaign performance</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you service-related notifications</li>
              <li>Improve and optimize the Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Google API Services User Data Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              Our use of information received from Google APIs adheres to the 
              <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-[#165581] hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                Google API Services User Data Policy
              </a>, including the Limited Use requirements.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              Specifically, we only use Google user data to:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Send emails on your behalf through your Gmail account</li>
              <li>Display your connected email accounts in the Service</li>
              <li>Verify your email address for authentication purposes</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              We do not use Google user data for advertising purposes or transfer it to third parties 
              except as necessary to provide the Service or as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-slate-600 leading-relaxed">We may share your information in the following situations:</p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., Stripe for payments, MongoDB for data storage)</li>
              <li><strong>Legal Requirements:</strong> If required by law or in response to valid legal processes</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Data Security</h2>
            <p className="text-slate-600 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information, including:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure storage of credentials and tokens</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              However, no method of transmission over the Internet is 100% secure. While we strive to 
              protect your information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to 
              provide you the Service. You may request deletion of your account and associated data at 
              any time by contacting us.
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              Campaign analytics and email tracking data are retained for 12 months to provide you with 
              historical performance insights.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Your Rights (GDPR)</h2>
            <p className="text-slate-600 leading-relaxed">
              If you are a resident of the European Economic Area (EEA) or United Kingdom, you have 
              certain data protection rights:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
              <li><strong>Portability:</strong> Request transfer of your data</li>
              <li><strong>Objection:</strong> Object to processing of your data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              To exercise these rights, please contact us at the email address below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Cookies</h2>
            <p className="text-slate-600 leading-relaxed">
              We use essential cookies to maintain your session and authentication state. We do not use 
              advertising or tracking cookies. By using the Service, you consent to our use of essential cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service is not intended for individuals under the age of 18. We do not knowingly collect 
              personal information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
              advised to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mt-4">
              <p className="text-slate-700 font-medium">eSay</p>
              <p className="text-slate-600">Email: privacy@esay.co.uk</p>
              <p className="text-slate-600">Website: https://esay.co.uk</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>&copy; {new Date().getFullYear()} eSay. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-[#165581]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#165581]">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
